import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import clsx from 'clsx';
import { Button } from './common/Button';

const MAX_TITLE_LENGTH = 200;
const MAX_DESCRIPTION_LENGTH = 5000;

interface FormErrors {
  title?: string;
  description?: string;
}

interface ProjectFormProps {
  initialTitle?: string;
  initialDescription?: string;
  submitLabel: string;
  cancelTo: string;
  isSubmitting: boolean;
  onSubmit: (data: { title: string; description: string }) => void;
}

export function ProjectForm({
  initialTitle = '',
  initialDescription = '',
  submitLabel,
  cancelTo,
  isSubmitting,
  onSubmit,
}: ProjectFormProps) {
  const [title, setTitle] = useState(initialTitle);
  const [description, setDescription] = useState(initialDescription);
  const [errors, setErrors] = useState<FormErrors>({});

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};
    if (!title.trim()) {
      newErrors.title = 'Title is required';
    } else if (title.length > MAX_TITLE_LENGTH) {
      newErrors.title = `Title must be ${MAX_TITLE_LENGTH} characters or less`;
    }
    if (!description.trim()) {
      newErrors.description = 'Description is required';
    } else if (description.length > MAX_DESCRIPTION_LENGTH) {
      newErrors.description = `Description must be ${MAX_DESCRIPTION_LENGTH} characters or less`;
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;
    onSubmit({ title: title.trim(), description: description.trim() });
  };

  return (
    <form onSubmit={handleSubmit} className="p-6 space-y-6">
      {/* Title Field */}
      <div>
        <label htmlFor="title" className="block text-sm font-medium text-wolf-300 mb-2">
          Title <span className="text-red-400">*</span>
        </label>
        <input
          id="title"
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Enter your idea title"
          className={clsx(
            'w-full px-4 py-3 rounded-lg bg-wolf-900/50 border text-wolf-100 placeholder-wolf-500',
            'focus:outline-none focus:ring-2 focus:ring-action focus:border-transparent',
            'transition-colors',
            errors.title ? 'border-red-500' : 'border-wolf-700/50'
          )}
          maxLength={MAX_TITLE_LENGTH + 10}
        />
        <div className="flex justify-between mt-1">
          {errors.title ? (
            <span className="text-sm text-red-400">{errors.title}</span>
          ) : (
            <span />
          )}
          <span className={clsx('text-sm', title.length > MAX_TITLE_LENGTH ? 'text-red-400' : 'text-wolf-500')}>
            {title.length}/{MAX_TITLE_LENGTH}
          </span>
        </div>
      </div>

      {/* Description Field */}
      <div>
        <label htmlFor="description" className="block text-sm font-medium text-wolf-300 mb-2">
          Description <span className="text-red-400">*</span>
        </label>
        <textarea
          id="description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Describe your idea in detail..."
          rows={6}
          className={clsx(
            'w-full px-4 py-3 rounded-lg bg-wolf-900/50 border text-wolf-100 placeholder-wolf-500',
            'focus:outline-none focus:ring-2 focus:ring-action focus:border-transparent',
            'transition-colors resize-none',
            errors.description ? 'border-red-500' : 'border-wolf-700/50'
          )}
          maxLength={MAX_DESCRIPTION_LENGTH + 100}
        />
        <div className="flex justify-between mt-1">
          {errors.description ? (
            <span className="text-sm text-red-400">{errors.description}</span>
          ) : (
            <span />
          )}
          <span className={clsx('text-sm', description.length > MAX_DESCRIPTION_LENGTH ? 'text-red-400' : 'text-wolf-500')}>
            {description.length}/{MAX_DESCRIPTION_LENGTH}
          </span>
        </div>
      </div>

      {/* Actions */}
      <div className="flex items-center gap-4 pt-4">
        <Button type="submit" loading={isSubmitting}>
          {submitLabel}
        </Button>
        <Link to={cancelTo} className="text-wolf-300 hover:text-wolf-100 transition-colors">
          Cancel
        </Link>
      </div>
    </form>
  );
}
